import { api } from "./apiService";

/**
 * One-element HTML5 audio adapter for the Drive Mode player injection seam.
 * Segment bytes are fetched with the existing authenticated blob helper.
 */
export function createDriveModeEngine({
  audioElement,
  fetchSegment = api.getAudioSegmentBlob,
  createObjectURL = (blob) => URL.createObjectURL(blob),
  revokeObjectURL = (objectUrl) => URL.revokeObjectURL(objectUrl),
} = {}) {
  if (!audioElement) throw new Error("Drive Mode requires one audio element");

  let objectUrl = null;
  let endedCallback = () => {};
  let destroyed = false;
  let playToken = 0;
  // Route-loss safety latch. While held, no play() (new OR already in-flight)
  // may reach HTMLMediaElement.play(); the real element is paused synchronously.
  let routeHeld = false;

  const releaseUrl = () => {
    if (objectUrl) {
      revokeObjectURL(objectUrl);
      objectUrl = null;
    }
  };
  const handleEnded = () => endedCallback();
  audioElement.addEventListener("ended", handleEnded);

  return {
    async play(segmentRef) {
      if (destroyed || routeHeld) return;
      const token = ++playToken;
      const blob = await fetchSegment(segmentRef);
      if (!(blob instanceof Blob)) {
        throw new Error(`segment request failed: ${blob && blob.status}`);
      }
      // A route loss (or destroy/advance) that landed while this fetch was
      // in-flight must never reach play(): routeHeld blocks it here, and an
      // explicit resume re-issues play() (bumping playToken), so a fetch
      // resolving even after release is dropped by the token check.
      if (destroyed || routeHeld || token !== playToken) return;
      releaseUrl();
      objectUrl = createObjectURL(blob);
      audioElement.src = objectUrl;
      await audioElement.play();
    },
    pause() {
      if (!destroyed) audioElement.pause();
    },
    resume() {
      if (!destroyed && !routeHeld) return audioElement.play();
      return undefined;
    },
    // Route-loss hold: synchronously pause the real element and latch so no
    // in-flight/delayed play() can reach HTMLMediaElement.play(). Idempotent.
    holdRoute() {
      if (destroyed) return;
      routeHeld = true;
      audioElement.pause();
    },
    // Release the route hold (only an explicit user play does this upstream).
    releaseRoute() {
      routeHeld = false;
    },
    isRouteHeld() {
      return routeHeld;
    },
    onEnded(callback) {
      endedCallback = typeof callback === "function" ? callback : () => {};
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      playToken += 1;
      audioElement.pause();
      audioElement.removeEventListener("ended", handleEnded);
      releaseUrl();
      audioElement.removeAttribute?.("src");
    },
  };
}

export default createDriveModeEngine;
